import { Fragment, useMemo, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { archiveReferralForMe, updateReferral } from '../lib/referrals';
import { useToast } from './Toast';

function formatDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString();
}

function currency(amount) {
  // NBNA: show values without hard-coding a currency (USD/CAD/etc).
  const num = Number(amount);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function ReferralDetailsModal({
  isOpen,
  onClose,
  referral,
  myMemberId,
  onUpdated,
}) {
  const { showToast } = useToast();

  const isReceiver = useMemo(() => {
    if (!referral || !myMemberId) return false;
    return String(referral.toMemberId) === String(myMemberId);
  }, [referral, myMemberId]);

  const isReferrer = useMemo(() => {
    if (!referral || !myMemberId) return false;
    return String(referral.fromMemberId) === String(myMemberId);
  }, [referral, myMemberId]);

  const [saving, setSaving] = useState(false);

  const [workflowContactAt, setWorkflowContactAt] = useState('');
  const [workflowEngagementAt, setWorkflowEngagementAt] = useState('');
  const [workflowEngagementNotes, setWorkflowEngagementNotes] = useState('');
  const [workflowSaleAmount, setWorkflowSaleAmount] = useState('');

  useEffect(() => {
    if (!referral) return;
    setWorkflowContactAt(referral.workflowContactAt ? new Date(referral.workflowContactAt).toISOString().slice(0, 16) : '');
    setWorkflowEngagementAt(referral.workflowEngagementAt ? new Date(referral.workflowEngagementAt).toISOString().slice(0, 16) : '');
    setWorkflowEngagementNotes(referral.workflowEngagementNotes || '');
    setWorkflowSaleAmount(referral.workflowSaleAmount != null ? String(referral.workflowSaleAmount) : '');
  }, [referral]);

  const canEditWorkflow = isReceiver;

  async function onSaveWorkflow(e) {
    e.preventDefault();
    if (!referral) return;
    if (!canEditWorkflow) return;

    setSaving(true);
    try {
      const patch = {
        workflowContactAt: workflowContactAt ? new Date(workflowContactAt).toISOString() : null,
        workflowEngagementAt: workflowEngagementAt ? new Date(workflowEngagementAt).toISOString() : null,
        workflowEngagementNotes,
        workflowSaleAmount: workflowSaleAmount === '' ? 0 : Number(workflowSaleAmount),
      };
      const updated = await updateReferral(referral.id, patch);
      showToast('Referral updated.', { kind: 'success' });
      onUpdated?.(updated);
    } catch (err) {
      showToast(err?.message || 'Failed to update referral.', { kind: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function onArchive() {
    if (!referral) return;
    const as = isReceiver ? 'receiver' : (isReferrer ? 'referrer' : null);
    if (!as) return;

    setSaving(true);
    try {
      const updated = await archiveReferralForMe(referral.id, { as });
      showToast('Archived.', { kind: 'success' });
      onUpdated?.(updated);
      onClose?.();
    } catch (err) {
      showToast(err?.message || 'Failed to archive.', { kind: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const steps = useMemo(() => {
    const contactDone = Boolean(referral?.workflowContactAt);
    const engagementDone = Boolean(referral?.workflowEngagementAt);
    const saleDone = Number(referral?.workflowSaleAmount || 0) > 0;

    return [
      { key: 'contact', label: 'Contact', done: contactDone },
      { key: 'engagement', label: 'Engagement', done: engagementDone },
      { key: 'sale', label: 'Sale', done: saleDone },
    ];
  }, [referral]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="flex items-start justify-between border-b px-6 py-4">
                  <div>
                    <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                      Referral Details
                    </Dialog.Title>
                    {referral?.counterparty?.businessName ? (
                      <div className="mt-1 text-sm text-gray-600">
                        With: <span className="font-medium">{referral.counterparty.businessName}</span>
                      </div>
                    ) : null}
                  </div>
                  <button type="button" className="text-sm text-gray-600" onClick={onClose}>Close</button>
                </div>

                <div className="px-6 py-5 space-y-6">
                  <section className="rounded-md border p-4">
                    <div className="text-sm font-medium text-gray-900">Contact</div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Name:</span> {referral?.contactName || '—'}</div>
                      <div><span className="text-gray-500">Company:</span> {referral?.company || '—'}</div>
                      <div><span className="text-gray-500">Email:</span> {referral?.contactEmail || '—'}</div>
                      <div><span className="text-gray-500">Phone:</span> {referral?.contactPhone || '—'}</div>
                    </div>
                    {referral?.notes ? (
                      <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">{referral.notes}</div>
                    ) : null}
                  </section>

                  <section className="rounded-md border p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">Workflow</div>
                      <div className="text-xs text-gray-500">Receiver can update</div>
                    </div>

                    <ol className="mt-3 flex flex-col sm:flex-row gap-2">
                      {steps.map((s) => (
                        <li key={s.key} className={`flex-1 rounded-md border px-3 py-2 text-sm ${s.done ? 'border-NBNA-primary bg-NBNA-primary/5' : 'border-gray-200 bg-white'}`}>
                          <div className="font-medium text-gray-900">{s.label}</div>
                          <div className="text-xs text-gray-600">
                            {s.key === 'contact' ? formatDateTime(referral?.workflowContactAt) : null}
                            {s.key === 'engagement' ? formatDateTime(referral?.workflowEngagementAt) : null}
                            {s.key === 'sale' ? currency(referral?.workflowSaleAmount) : null}
                          </div>
                        </li>
                      ))}
                    </ol>

                    <form className="mt-4 space-y-3" onSubmit={onSaveWorkflow}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600" htmlFor="wf-contact-at">Contact date</label>
                          <input
                            id="wf-contact-at"
                            type="datetime-local"
                            className="input-field mt-1 w-full"
                            value={workflowContactAt}
                            onChange={(e) => setWorkflowContactAt(e.target.value)}
                            disabled={!canEditWorkflow || saving}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600" htmlFor="wf-engagement-at">Engagement date</label>
                          <input
                            id="wf-engagement-at"
                            type="datetime-local"
                            className="input-field mt-1 w-full"
                            value={workflowEngagementAt}
                            onChange={(e) => setWorkflowEngagementAt(e.target.value)}
                            disabled={!canEditWorkflow || saving}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600" htmlFor="wf-engagement-notes">Engagement notes</label>
                        <textarea
                          id="wf-engagement-notes"
                          className="input-field mt-1 w-full"
                          rows={3}
                          value={workflowEngagementNotes}
                          onChange={(e) => setWorkflowEngagementNotes(e.target.value)}
                          disabled={!canEditWorkflow || saving}
                          placeholder="Visited store, received quote, set meeting, etc."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600" htmlFor="wf-sale-amount">Sale amount</label>
                          <input
                            id="wf-sale-amount"
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field mt-1 w-full"
                            value={workflowSaleAmount}
                            onChange={(e) => setWorkflowSaleAmount(e.target.value)}
                            disabled={!canEditWorkflow || saving}
                          />
                        </div>
                        <div className="flex items-end justify-end gap-3">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={onArchive}
                            disabled={saving || (!isReceiver && !isReferrer)}
                          >
                            Archive for me
                          </button>
                          <button
                            type="submit"
                            className="btn-primary"
                            disabled={saving || !canEditWorkflow}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>

                      {!canEditWorkflow ? (
                        <div className="text-xs text-gray-500">You can view only. The receiver updates workflow/sale.</div>
                      ) : null}
                    </form>
                  </section>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
